import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, User } from 'lucide-react';
import axios from 'axios';
import posthog from 'posthog-js';
import API_URL from '../config';
import NotificationBell from './Notifications/NotificationBell';

function trimValue(value) {
  if (value == null) return '';
  return String(value).trim();
}

function buildAuthorityLabel(name, dot) {
  const n = trimValue(name);
  const d = trimValue(dot);
  if (n && d) return `${n} · ${d}`;
  if (n) return n;
  if (d) return `DOT ${d}`;
  return 'Add your company';
}

/**
 * Top bar: company/authority selector, search, alerts, user menu.
 * Design: calm, enterprise; no gradients.
 */
export default function Topbar() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authorityLabel, setAuthorityLabel] = useState('Add your company');
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let storedUser = null;
    const raw = localStorage.getItem('user');
    if (raw) {
      try {
        storedUser = JSON.parse(raw);
        setUser(storedUser);
      } catch {
        storedUser = null;
      }
    }

    let name = trimValue(storedUser?.companyInfo?.companyName);
    let dot =
      trimValue(storedUser?.driverInfo?.dotNumber) ||
      trimValue(storedUser?.companyInfo?.dotNumber) ||
      trimValue(storedUser?.dotNumber);

    const applyLabel = (nextName, nextDot) => {
      if (!cancelled) setAuthorityLabel(buildAuthorityLabel(nextName, nextDot));
    };

    applyLabel(name, dot);

    const token = localStorage.getItem('token');
    if (!name && token) {
      axios
        .get(`${API_URL}/api/authority`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (cancelled) return;
          const first = Array.isArray(res.data) ? res.data[0] : null;
          if (!first) return;
          const fromAuthority =
            trimValue(first.dbaName) || trimValue(first.legalName);
          if (!name) name = fromAuthority;
          if (!dot) dot = trimValue(first.dotNumber);
          applyLabel(name, dot);
        })
        .catch(() => {
          /* keep localStorage-derived label */
        });
    }

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <header className="topbar">
      <div className="topbar__left">
        <button
          type="button"
          className="topbar__selector"
          onClick={() => navigate('/authority')}
          title="Authority / company"
        >
          <span className="topbar__selector-label">{authorityLabel}</span>
          <ChevronDown size={16} className="topbar__selector-chevron" />
        </button>
        <input
          type="search"
          className="topbar__search"
          placeholder="Search..."
          aria-label="Search"
        />
      </div>
      <div className="topbar__right">
        <NotificationBell />
        <button
          type="button"
          className="topbar__icon-btn"
          onClick={() => setUserMenuOpen(!userMenuOpen)}
          aria-label="User menu"
          aria-expanded={userMenuOpen}
        >
          <User size={20} />
        </button>
        {userMenuOpen && (
          <>
            <div
              className="ui-drawer-backdrop"
              style={{ zIndex: 101 }}
              onClick={() => setUserMenuOpen(false)}
              aria-hidden
            />
            <div className="topbar__user-menu" role="menu">
              <div className="topbar__user-menu-head">
                {user?.firstName || user?.name || 'User'}
                <span className="text-small text-muted">{user?.email}</span>
              </div>
              <button type="button" role="menuitem" onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}>
                Settings
              </button>
              <button type="button" role="menuitem" onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}>
                Billing
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  posthog.reset();
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  navigate('/');
                  window.location.reload();
                }}
              >
                Log out
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
